/**
 * AdminLeadHunter — Google-Places-Scout für Admins: neue Betriebe finden und nach
 * allgemeinen Regeln als Aufgabe „Besuchen“ an Agenten verteilen. Bereits vorhandene
 * Kontakte/Agenten-Aufgaben sind nicht zuweisbar (keine Dubletten).
 * Gestaltung: sevdesk-DNA (docs/design-dna/ausgaben-sevdesk.json), PageShell nach DESIGN.md.
 */
import { useState } from 'react';
import { AlertCircle, Loader2, Radar, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import { BTN_PRIMARY, BTN_SMALL, CARD } from '@/components/ausgaben/dna';
import StatusTabs from '@/components/ausgaben/StatusTabs';
import { useLeadHunter, MAX_NEW } from '@/components/leadhunter/useLeadHunter';
import SearchPanel from '@/components/leadhunter/SearchPanel';
import ImportSummary from '@/components/leadhunter/ImportSummary';
import { LeadRow, BlockedRow, SkeletonRows } from '@/components/leadhunter/LeadRows';
import { cn } from '@/lib/utils';

function AssignButton({ hunter, className }) {
  const { selected, importing } = hunter;
  return (
    <Button className={cn(BTN_PRIMARY, className)} disabled={!selected.size || importing} onClick={hunter.importSelected}>
      {importing ? <><Loader2 className="h-4 w-4 animate-spin" /> Weise zu…</> : <><UserPlus className="h-4 w-4" /> {selected.size} zuweisen</>}
    </Button>
  );
}

function EmptyState({ searching, tab }) {
  if (searching) return null;
  return (
    <div className="px-6 py-14 text-center">
      <Radar className="mx-auto mb-3 h-8 w-8 text-dna-placeholder" />
      <p className="text-[15px] font-semibold text-dna-primary">
        {tab === 'vorhanden' ? 'Keine bereits vorhandenen Treffer' : 'Noch keine Treffer'}
      </p>
      <p className="mx-auto mt-1 max-w-sm text-[13px] text-dna-muted">
        Branche und Ort wählen, suchen – neue Betriebe werden sofort angezeigt und nach Regel einem Agenten vorgeschlagen.
      </p>
    </div>
  );
}

export default function AdminLeadHunter() {
  const hunter = useLeadHunter();
  const [tab, setTab] = useState('neu');
  const { results, blocked, selected, assignments, agents, stats, isSearching, detailProgress } = hunter;
  const withoutAgent = results.filter((l) => selected.has(l.google_place_id) && !assignments[l.google_place_id]).length;
  const hasResults = results.length > 0 || blocked.length > 0;
  const rows = tab === 'neu' ? results : blocked;

  return (
    <PageShell
      className="bg-dna-bg"
      mainClassName="pb-32 lg:pb-24"
      head={(
        <PageHeader
          title="Lead Hunter"
          subtitle={`${agents.length} aktive Agenten · bis ${MAX_NEW} neue Treffer`}
          actions={results.length > 0 && <AssignButton hunter={hunter} className="hidden sm:inline-flex" />}
        />
      )}
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[22rem_minmax(0,1fr)] lg:items-start lg:gap-6">
        <SearchPanel hunter={hunter} hasResults={hasResults} />

        <div className="min-w-0 space-y-4">
          {hunter.error && (
            <p role="alert" className="flex items-start gap-2 rounded-xl border border-dna-due-fg/20 bg-dna-due-bg p-3 text-[14px] text-dna-due-fg">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{hunter.error}
            </p>
          )}
          {hunter.warning && <p className="rounded-xl bg-dna-open-bg p-3 text-[13px] text-dna-open-fg">{hunter.warning}</p>}
          <ImportSummary result={hunter.importResult} onClose={hunter.clearImportResult} />

          <section className={cn(CARD, 'overflow-hidden')} aria-label="Gefundene Leads">
            <div className="flex items-center justify-between gap-2 border-b border-dna-line px-3 py-2.5">
              <StatusTabs ariaLabel="Treffer" value={tab} onChange={setTab} mobileKeys={['neu', 'vorhanden']}
                tabs={[{ key: 'neu', label: 'Neu', count: results.length }, { key: 'vorhanden', label: 'Bereits vorhanden', count: blocked.length }]} />
              {tab === 'neu' && results.length > 0 && (
                <Button variant="ghost" className={BTN_SMALL} onClick={hunter.toggleAll}>
                  {selected.size === results.length ? 'Keine' : 'Alle'}<span className="hidden sm:inline"> auswählen</span>
                </Button>
              )}
            </div>

            {(isSearching || detailProgress || (tab === 'neu' && results.length > 0)) && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-dna-line bg-dna-warm px-4 py-2 text-[13px] text-dna-muted" aria-live="polite">
                {isSearching && <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Suche in Google Places…</span>}
                {!isSearching && tab === 'neu' && results.length > 0 && <span className="tabular-nums">{selected.size} von {results.length} ausgewählt</span>}
                {withoutAgent > 0 && <span className="font-semibold text-dna-open-fg">{withoutAgent} ohne Agent – werden übersprungen</span>}
                {detailProgress && <span className="inline-flex items-center gap-1.5 tabular-nums"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Kontaktdaten {detailProgress.done}/{detailProgress.total}</span>}
              </div>
            )}

            {tab === 'vorhanden' && blocked.length > 0 && (
              <p className="border-b border-dna-line px-4 py-2 text-[13px] text-dna-muted">Diese Betriebe sind schon Kontakt oder in einer Agenten-Aufgabe und können nicht erneut zugewiesen werden.</p>
            )}

            {rows.length === 0 ? (
              isSearching ? <ul className="divide-y divide-dna-line"><SkeletonRows /></ul> : <EmptyState searching={isSearching} tab={tab} />
            ) : (
              <ul className="divide-y divide-dna-line">
                {tab === 'neu'
                  ? results.map((lead) => (
                    <LeadRow key={lead.google_place_id} lead={lead} agents={agents} stats={stats}
                      checked={selected.has(lead.google_place_id)} onToggle={hunter.toggle}
                      agent={assignments[lead.google_place_id]} onAgent={hunter.setAgent} />
                  ))
                  : blocked.map((lead) => <BlockedRow key={lead.google_place_id || lead.name} lead={lead} agents={agents} />)}
              </ul>
            )}
          </section>
        </div>
      </div>

      {/* Mobil: feste Aktionsleiste über der Bottom-Navigation */}
      {results.length > 0 && (
        <div className="fixed inset-x-0 bottom-16 z-30 border-t border-dna-line bg-white px-4 py-3 shadow-[0_-4px_12px_rgba(34,34,67,0.08)] sm:hidden">
          <div className="mx-auto flex max-w-lg items-center gap-3">
            <p className="min-w-0 flex-1 text-[13px] text-dna-muted"><span className="font-semibold tabular-nums text-dna-primary">{selected.size}</span> ausgewählt</p>
            <AssignButton hunter={hunter} className="flex-1" />
          </div>
        </div>
      )}
    </PageShell>
  );
}
